'use client';

import { useToolUsageReport } from '@/components/tools/use-tool-usage-report';
import { useEffect, useRef, useState } from 'react';

function getBaseName(name: string) {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return function random() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export default function BrowserGlitchImageMaker() {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [fileName, setFileName] = useState('image');
  const [intensity, setIntensity] = useState(45);
  const [noiseOn, setNoiseOn] = useState(true);
  const [seed, setSeed] = useState(1);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  useToolUsageReport('glitch-image-maker', Boolean(downloadUrl));
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    return () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  function handleFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setImage(img);
      setFileName(getBaseName(file.name) || 'image');
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      setError('This image format is not supported in your browser.');
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }

  useEffect(() => {
    if (!image || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const w = image.naturalWidth;
    const h = image.naturalHeight;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(image, 0, 0);

    const t = intensity / 100;
    const rand = mulberry32(seed * 2654435761 + Math.round(intensity));
    const source = ctx.getImageData(0, 0, w, h);
    const src = source.data;
    const out = ctx.createImageData(w, h);
    const dst = out.data;

    const shift = Math.max(1, Math.round(t * 0.05 * w));
    const noiseAmp = noiseOn ? t * 70 : 0;

    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        const i = (y * w + x) * 4;
        const rx = Math.min(w - 1, Math.max(0, x - shift));
        const bx = Math.min(w - 1, Math.max(0, x + shift));
        const rI = (y * w + rx) * 4;
        const bI = (y * w + bx) * 4;
        let r = src[rI];
        let g = src[i + 1];
        let b = src[bI + 2];
        if (noiseAmp > 0) {
          const n = (rand() - 0.5) * noiseAmp;
          r += n;
          g += n;
          b += n;
        }
        dst[i] = r < 0 ? 0 : r > 255 ? 255 : r;
        dst[i + 1] = g < 0 ? 0 : g > 255 ? 255 : g;
        dst[i + 2] = b < 0 ? 0 : b > 255 ? 255 : b;
        dst[i + 3] = src[i + 3];
      }
    }
    ctx.putImageData(out, 0, 0);

    // Random horizontal slice offsets, drawn from a snapshot of the shifted result.
    const bandCount = Math.round(t * 36);
    if (bandCount > 0) {
      const snapshot = document.createElement('canvas');
      snapshot.width = w;
      snapshot.height = h;
      const snapCtx = snapshot.getContext('2d');
      if (snapCtx) {
        snapCtx.drawImage(canvas, 0, 0);
        for (let n = 0; n < bandCount; n += 1) {
          const bandY = Math.floor(rand() * h);
          const bandH = Math.max(2, Math.floor(rand() * 0.06 * h) + 2);
          const offset = Math.round((rand() - 0.5) * t * 0.3 * w);
          ctx.clearRect(0, bandY, w, bandH);
          ctx.drawImage(snapshot, 0, bandY, w, bandH, offset, bandY, w, bandH);
        }
      }
    }

    canvas.toBlob((blob) => {
      if (blob) {
        setDownloadUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      }
    }, 'image/png');
  }, [image, intensity, noiseOn, seed]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Glitch</span>
        <h2 className="tool-live-title">Glitch image maker</h2>
        <p className="tool-live-subtitle">
          Upload an image and add an RGB-shift datamosh glitch with random slicing and noise. Dial
          the intensity, re-randomize for a fresh look, and download. Runs in your browser and
          nothing is uploaded to a server.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Choose an image</span>
            <input
              accept="image/*"
              onChange={(event) => handleFile(event.target.files?.[0])}
              type="file"
            />
          </label>

          {image ? (
            <div className="tool-live-controls">
              <label className="tool-live-field tool-live-field-full">
                <span>Intensity: {intensity}%</span>
                <input
                  max={100}
                  min={0}
                  onChange={(event) => setIntensity(Number(event.target.value))}
                  step={1}
                  type="range"
                  value={intensity}
                />
              </label>
              <label className="tool-live-field tool-live-field-full">
                <span>
                  <input
                    checked={noiseOn}
                    onChange={(event) => setNoiseOn(event.target.checked)}
                    type="checkbox"
                  />{' '}
                  Add noise
                </span>
              </label>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => setSeed((value) => value + 1)}
                  type="button"
                >
                  Re-randomize
                </button>
              </div>
            </div>
          ) : null}

          <div className="tool-live-actions">
            {downloadUrl ? (
              <a
                className="button button-secondary"
                download={`${fileName}-glitch.png`}
                href={downloadUrl}
              >
                Download PNG
              </a>
            ) : null}
          </div>

          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          {image ? null : <p className="tool-live-panel-note">Pick an image to glitch it.</p>}
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              display: image ? 'block' : 'none',
            }}
          />
          <p className="tool-live-panel-note">
            Processing runs locally with an HTML canvas. Your image never leaves your device.
          </p>
        </section>
      </div>
    </div>
  );
}
